import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import { Panel } from '@/components/ui/Panel';
import { aed, num, shortDate } from '@/lib/format';
import { portalOrders } from '@/mocks/portal';

const orderTotal = (lines: { qty: number; price: number }[]) =>
  lines.reduce((s, l) => s + l.qty * l.price, 0);
const orderUnits = (lines: { qty: number }[]) => lines.reduce((s, l) => s + l.qty, 0);

type Filter = 'All' | 'New' | 'AwaitingPayment' | 'ReadyForPickup' | 'Closed';

export default function PortalOrdersPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('All');

  const filters: Filter[] = ['All', 'New', 'AwaitingPayment', 'ReadyForPickup', 'Closed'];

  const rows = useMemo(
    () => portalOrders.filter((o) => filter === 'All' || o.status === filter),
    [filter],
  );

  const openCount = portalOrders.filter((o) => o.status !== 'Closed').length;
  const confirmCount = portalOrders.filter((o) => o.status === 'New').length;
  const pickupCount = portalOrders.filter((o) => o.status === 'ReadyForPickup').length;
  const monthTotal = portalOrders
    .filter((o) => o.date.startsWith('2026-10'))
    .reduce((s, o) => s + orderTotal(o.lines), 0);

  const kpis = [
    { label: t('portal.orders.kpiOpen'), value: String(openCount), icon: 'ri-shopping-bag-3-line' },
    { label: t('portal.orders.kpiConfirm'), value: String(confirmCount), icon: 'ri-question-answer-line', accent: true },
    { label: t('portal.orders.kpiPickup'), value: String(pickupCount), icon: 'ri-truck-line' },
    { label: t('portal.orders.kpiMonth'), value: aed(monthTotal), icon: 'ri-line-chart-line' },
  ];

  return (
    <div>
      <div className="mb-4 border-b border-background-200 pb-4">
        <h1 className="text-[20px] font-semibold leading-6 text-foreground-950">{t('portal.orders.title')}</h1>
        <p className="mt-1 max-w-3xl text-[12.5px] leading-5 text-foreground-500">{t('portal.orders.subtitle')}</p>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-foreground-500">{k.label}</p>
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${
                  k.accent ? 'bg-accent-50 text-accent-700' : 'bg-primary-50 text-primary-700'
                }`}
              >
                <i className={`${k.icon} text-[13px]`} />
              </span>
            </div>
            <p className="tnum mt-1.5 text-[19px] font-semibold text-foreground-950">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-1 rounded-md border border-background-200 bg-background-100 p-1">
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`cursor-pointer whitespace-nowrap rounded px-2.5 py-1 text-[11.5px] font-medium transition-colors ${
              filter === f ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
            }`}
          >
            {f === 'All' ? t('portal.common.all') : t(`portal.status.${f}`)}
          </button>
        ))}
      </div>

      <Panel padded={false} className="hidden lg:block">
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[880px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2.5 font-medium">{t('portal.orders.colPo')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.orders.colDate')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.orders.colItems')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('portal.orders.colUnits')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('portal.orders.colAmount')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.orders.colStatus')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.po} className="border-b border-background-200/70 last:border-0 hover:bg-primary-50/30">
                  <td className="px-4 py-3">
                    <Link to={`/portal/orders/${o.po}`} className="tnum text-[12px] font-semibold text-foreground-950 hover:text-primary-700">
                      {o.po}
                    </Link>
                    {o.readyDate && (
                      <p className="tnum mt-0.5 text-[10px] text-foreground-400">
                        {t('portal.order.readyDateLabel')} {shortDate(o.readyDate)}
                      </p>
                    )}
                  </td>
                  <td className="tnum px-3 py-3 text-[11.5px] text-foreground-600">{shortDate(o.date)}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {o.lines.map((l) => (
                        <span
                          key={l.sku}
                          className="tnum rounded border border-background-200 bg-background-100 px-1.5 py-0.5 text-[10px] font-medium text-foreground-700"
                        >
                          {l.sku} × {l.qty}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="tnum px-3 py-3 text-right text-foreground-700">{orderUnits(o.lines)}</td>
                  <td className="tnum px-3 py-3 text-right font-semibold text-foreground-950">{aed(orderTotal(o.lines))}</td>
                  <td className="px-3 py-3">
                    <PortalStatus status={o.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/portal/orders/${o.po}`}
                      className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded border border-background-300 bg-background-50 px-2 text-[10.5px] font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    >
                      {t('portal.orders.open')}
                      <i className="ri-arrow-right-line text-[11px]" />
                    </Link>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                    {t('portal.orders.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-1 border-t border-background-200 px-4 py-2.5 text-[10.5px] text-foreground-400 sm:flex-row sm:items-center sm:justify-between">
          <p>
            {t('portal.common.showing', { shown: rows.length, total: portalOrders.length })} ·{' '}
            {num(portalOrders.reduce((s, o) => s + orderUnits(o.lines), 0))} {t('portal.common.units')}
          </p>
          <p>{t('portal.orders.note')}</p>
        </div>
      </Panel>

      <div className="flex flex-col gap-2.5 lg:hidden">
        {rows.map((o) => (
          <Link
            key={o.po}
            to={`/portal/orders/${o.po}`}
            className="rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:bg-background-100/50"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="tnum text-[12.5px] font-semibold text-foreground-950">{o.po}</p>
                <p className="tnum text-[10.5px] text-foreground-400">{shortDate(o.date)}</p>
              </div>
              <PortalStatus status={o.status} className="!px-1.5 !text-[10px]" />
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {o.lines.map((l) => (
                <span
                  key={l.sku}
                  className="tnum rounded border border-background-200 bg-background-100 px-1.5 py-0.5 text-[10px] font-medium text-foreground-700"
                >
                  {l.sku} × {l.qty}
                </span>
              ))}
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-background-200 pt-2">
              <span className="text-[10.5px] text-foreground-500">
                {orderUnits(o.lines)} {t('portal.common.units')}
              </span>
              <span className="tnum flex items-center gap-1 text-[13px] font-semibold text-foreground-950">
                {aed(orderTotal(o.lines))}
                <i className="ri-arrow-right-s-line text-[14px] text-foreground-400" />
              </span>
            </div>
          </Link>
        ))}
        {rows.length === 0 && (
          <p className="rounded-lg border border-background-200 bg-background-50 px-3 py-8 text-center text-[12px] text-foreground-500">
            {t('portal.orders.empty')}
          </p>
        )}
        <p className="mt-1 text-[10.5px] leading-4 text-foreground-400">{t('portal.orders.note')}</p>
      </div>
    </div>
  );
}